// src/components/FloatingPianoOverlay.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VirtualPiano from "./VirtualPiano";

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

export default function FloatingPianoOverlay({
  open,
  windowSize,
  hasStarted,

  // (levelId kept optional; not required anymore)
  levelId,

  firstKey,
  lastKey,

  pianoConfig,
  onNoteClick,

  showKeyGlow,
  onToggleKeyGlow,

  onDock,

  // persistence handled by parent (kept for backwards compatibility)
  initialPrefs = { size: "l", x: null, y: null },
  onPrefsChange,

  // If you pass this prop, it wins; otherwise we fallback
  showAllBlackLabels,
  blackKeyLabels = {},
}) {
  // Base scale + fixed LARGE factor (no resizing UI)
  const FLOAT_BASE_SCALE = 0.55;
  const LARGE_FACTOR = 1.15; // previously "L"
  const EXTRA_SCALE_25 = 1.25; // ✅ +25% overall
  const SNAP_MARGIN = 12;

  const [snapKey, setSnapKey] = useState("center");
  const [snapAnimating, setSnapAnimating] = useState(false);

  const boxRef = useRef(null);
  const posRef = useRef({ x: 0, y: 0 });

  const [pos, setPos] = useState(() => ({ x: SNAP_MARGIN, y: SNAP_MARGIN }));

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const getDims = useCallback(() => {
    const rect = boxRef.current?.getBoundingClientRect();
    return {
      w: rect?.width ?? 520,
      h: rect?.height ?? 240,
    };
  }, []);

  const getSnapPoints = useCallback(() => {
    const vw =
      windowSize?.width ??
      (typeof window !== "undefined" ? window.innerWidth : 1024);
    const vh =
      windowSize?.height ??
      (typeof window !== "undefined" ? window.innerHeight : 768);

    const { w, h } = getDims();
    const y = Math.max(SNAP_MARGIN, vh - h - SNAP_MARGIN);

    const left = { x: SNAP_MARGIN, y };
    const right = { x: Math.max(SNAP_MARGIN, vw - w - SNAP_MARGIN), y };
    const center = { x: Math.max(SNAP_MARGIN, (vw - w) / 2), y };

    return { left, center, right, vw, vh, w, h };
  }, [getDims, windowSize]);

  const clampPos = useCallback(
    (x, y) => {
      const { vw, vh, w, h } = getSnapPoints();
      const cx = clamp(
        x,
        SNAP_MARGIN,
        Math.max(SNAP_MARGIN, vw - w - SNAP_MARGIN)
      );
      const cy = clamp(
        y,
        SNAP_MARGIN,
        Math.max(SNAP_MARGIN, vh - h - SNAP_MARGIN)
      );
      return { x: cx, y: cy };
    },
    [getSnapPoints]
  );

  const applySnap = useCallback(
    (key) => {
      const pts = getSnapPoints();
      const target = pts[key] || pts.center;
      const clamped = clampPos(target.x, target.y);

      setSnapAnimating(true);
      setSnapKey(key);
      setPos(clamped);

      window.setTimeout(() => setSnapAnimating(false), 260);
    },
    [getSnapPoints, clampPos]
  );

  const nearestSnapKeyFromXY = useCallback(
    (x, y) => {
      const pts = getSnapPoints();
      const candidates = [
        { k: "left", p: pts.left },
        { k: "center", p: pts.center },
        { k: "right", p: pts.right },
      ];

      let best = candidates[0];
      let bestD = Infinity;

      for (const c of candidates) {
        const dx = x - c.p.x;
        const dy = y - c.p.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      return best.k;
    },
    [getSnapPoints]
  );

  const didInitRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (didInitRef.current) return;
    didInitRef.current = true;

    const pts = getSnapPoints();

    const hasSaved =
      Number.isFinite(initialPrefs?.x) && Number.isFinite(initialPrefs?.y);

    const startKey = hasSaved
      ? nearestSnapKeyFromXY(initialPrefs.x, initialPrefs.y)
      : "center";

    const target = pts[startKey] || pts.center;
    const startPos = clampPos(target.x, target.y);

    setSnapKey(startKey);
    setPos(startPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const pts = getSnapPoints();
    const target = pts[snapKey] || pts.center;
    setPos(clampPos(target.x, target.y));
  }, [
    open,
    snapKey,
    windowSize?.width,
    windowSize?.height,
    getSnapPoints,
    clampPos,
  ]);

  // Persist prefs (keep size="l" for storage compatibility)
  useEffect(() => {
    if (!open) return;
    onPrefsChange?.({
      size: "l",
      x: posRef.current.x,
      y: posRef.current.y,
    });
  }, [open, pos.x, pos.y, onPrefsChange]);

  const scaledConfig = useMemo(() => {
    const scale = FLOAT_BASE_SCALE * LARGE_FACTOR * EXTRA_SCALE_25;

    const baseKW = pianoConfig?.keyWidth ?? 38;
    const baseWH = pianoConfig?.whiteKeyHeight ?? 150;
    const baseBKW = pianoConfig?.blackKeyWidth ?? 24;
    const baseBKH = pianoConfig?.blackKeyHeight ?? 96;

    const keyWidth = clamp(Math.round(baseKW * scale), 14, 52);
    const whiteKeyHeight = clamp(Math.round(baseWH * scale), 70, 180);
    const blackKeyWidth = clamp(Math.round(baseBKW * scale), 10, 40);
    const blackKeyHeight = clamp(Math.round(baseBKH * scale), 45, 130);

    return { keyWidth, whiteKeyHeight, blackKeyWidth, blackKeyHeight };
  }, [pianoConfig]);

  // ✅ Updated fallback: Level 3+ shows black labels (matches V1.1 rules)
  const effectiveShowAllBlackLabels =
    typeof showAllBlackLabels === "boolean"
      ? showAllBlackLabels
      : (levelId ?? 1) >= 3;

  const reset = useCallback(() => {
    applySnap("center");
  }, [applySnap]);

  if (!open) return null;

  const snapButtons = [
    { key: "left", label: "Left", icon: "⬅︎" },
    { key: "center", label: "Center", icon: "≡" },
    { key: "right", label: "Right", icon: "➡︎" },
  ];

  return (
    <div
      ref={boxRef}
      className={"fixed z-[5000] " + (hasStarted ? "" : "opacity-95")}
      style={{
        left: pos.x,
        top: pos.y,
        width: "fit-content",
        maxWidth: "92vw",
        transition: snapAnimating ? "left 240ms ease, top 240ms ease" : "none",
      }}
    >
      <div className="rounded-[18px] overflow-hidden shadow-2xl border border-white/70 bg-white/92 backdrop-blur">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-black text-slate-800">
              🎹 Floating Piano
            </div>

            <div className="flex items-center gap-1">
              {snapButtons.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => applySnap(b.key)}
                  className={
                    "px-2 py-[3px] rounded-full text-[9px] font-extrabold border " +
                    (snapKey === b.key
                      ? "bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50")
                  }
                  title={`Snap ${b.label.toLowerCase()}`}
                  aria-label={`Snap ${b.label.toLowerCase()}`}
                >
                  <span className="inline-block w-[14px] text-center">
                    {b.icon}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              className="px-2 py-[3px] rounded-full text-[9px] font-extrabold border bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={reset}
            >
              Reset
            </button>

            <button
              type="button"
              className="px-2 py-[3px] rounded-full text-[9px] font-extrabold border bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => onDock?.()}
            >
              Dock
            </button>
          </div>
        </div>

        {/* Piano area */}
        <div className="p-1.5">
          {!hasStarted && (
            <div className="mb-1 text-center text-[9px] text-slate-600">
              Tap <strong>Play Now</strong> first.
            </div>
          )}

          <div
            className={
              "w-full bg-white rounded-[14px] border border-slate-100 " +
              "px-1 py-1 flex items-center justify-center " +
              (!hasStarted ? "opacity-85 pointer-events-none" : "")
            }
            style={{
              maxWidth: "92vw",
              overflow: "hidden",
            }}
          >
            <div className="inline-flex items-center justify-center">
              <VirtualPiano
                onNoteClick={onNoteClick}
                keyWidth={scaledConfig.keyWidth}
                whiteKeyHeight={scaledConfig.whiteKeyHeight}
                blackKeyWidth={scaledConfig.blackKeyWidth}
                blackKeyHeight={scaledConfig.blackKeyHeight}
                firstKey={firstKey}
                lastKey={lastKey}
                showAllBlackLabels={effectiveShowAllBlackLabels}
                blackKeyLabels={blackKeyLabels}
              />
            </div>
          </div>

          <div className="mt-1 flex items-center justify-between text-[9px] text-slate-600">
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                className="h-3 w-3 accent-[#3B82F6]"
                checked={!!showKeyGlow}
                onChange={(e) => onToggleKeyGlow?.(e.target.checked)}
              />
              <span>Key glow</span>
            </label>

            <span className="text-slate-400">
              {snapKey === "left"
                ? "Left"
                : snapKey === "right"
                ? "Right"
                : "Center"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
